import AccountBalance from "@mui/icons-material/AccountBalance";
import HikingIcon from "@mui/icons-material/Hiking";
import Mail from "@mui/icons-material/Mail";
import MonetizationOn from "@mui/icons-material/MonetizationOn";
import SurfingIcon from "@mui/icons-material/Surfing";
import { type NavItem } from "components/LeftNavV2/LeftNavDrawerV2";
import { GroupNavItem, StandardNavItem } from "components/LeftNavV2/NavItemV2";
import { type ElementType, useMemo } from "react";
import { useCustomerNavSectionsV2 } from "routes/Customer/customerNavSectionsV2";
import { useStaffNavSectionsV2 } from "routes/Staff/staffNavSectionsV2";

import { useCurrentUser } from "./useCurrentUser";
import { useTenant } from "./useTenant";

type LandingPageSection = {
  title: string;
  longTitle?: string;
  items: (StandardNavItem | GroupNavItem)[];
};

function isLandingPageNavItem(item: NavItem): item is StandardNavItem | GroupNavItem {
  return item.kind === "Standard" || item.kind === "Group";
}

export type LandingPageSections = {
  loading: boolean;
  sections: LandingPageSection[];
};

const customerSectionsToInclude: string[] = ["Programs", "Toolkit", "Catalogs"];

const staffSectionToInclude = "Staff";

// Subset of staff nav children to surface as landing-page tiles, with the icon
// each tile should display. The staff sidebar represents these as group
// children (which have no Icon), so the landing page supplies its own.
const staffLandingTiles: { text: string; Icon: ElementType }[] = [
  { text: "Fulfillment", Icon: HikingIcon },
  { text: "Organizations", Icon: SurfingIcon },
  { text: "Agencies", Icon: AccountBalance },
  { text: "Mail Room", Icon: Mail },
  { text: "Payments", Icon: MonetizationOn },
];

export function useLandingPageSections(): LandingPageSections {
  const { tenant } = useTenant();

  const { isStaff, loading: loadingUser } = useCurrentUser();
  const { navItems: sections } = useCustomerNavSectionsV2();
  const staffNavSections = useStaffNavSectionsV2();

  const customerSections = useMemo(() => {
    const includedSections = sections.filter((section) =>
      customerSectionsToInclude.includes(section.title)
    );
    return includedSections.map((section) => ({
      ...section,
      items: section.items.filter(isLandingPageNavItem),
    }));
  }, [sections]);

  // Show a subset of the staff tools on the landing page. Walk the staff
  // sidebar groups to find each desired tile and re-render it as a
  // StandardNavItem with a landing-page-appropriate icon.
  const filteredStaffSections: LandingPageSection[] = useMemo(() => {
    const sections = staffNavSections.filter((section) => section.title === staffSectionToInclude);

    return sections.map((section) => {
      const tiles: StandardNavItem[] = [];
      for (const { text, Icon } of staffLandingTiles) {
        for (const item of section.items) {
          if (item.kind === "Standard" && item.text === text) {
            tiles.push({ ...item, Icon });
            break;
          }
          if (item.kind === "Group") {
            const child = item.children.find((c) => c.text === text);
            if (child) {
              tiles.push({
                kind: "Standard",
                text: child.text,
                path: child.path,
                description: child.description,
                textColor: child.textColor,
                Icon,
              });
              break;
            }
          }
        }
      }

      return { ...section, items: tiles };
    });
  }, [staffNavSections]);

  const navItems = useMemo(() => {
    if (!isStaff) {
      return customerSections;
    }

    if (tenant) {
      return [...customerSections, ...filteredStaffSections];
    } else {
      return filteredStaffSections;
    }
  }, [tenant, customerSections, filteredStaffSections, isStaff]);

  return {
    loading: loadingUser,
    sections: navItems,
  };
}
